import { GroupButton, Icons, ReactIcons } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MeetingRoomAudienceType } from "@kalyzee/kast-web-sdk/websocket";
import { openMeetingActivityRoom, startRoomHighlight } from "../../../helpers/meeting";
import { useMeetingSelector } from "../../../hooks/meeting";
import { ExpertView, PopulatedMeetingRoom } from "../../../interfaces/populatedData";
import { ExpertsToRemotesContainer, ExpertsToRemotesContainerProps } from "../../player/container/ExpertsToRemotesContainer";
import { PedagogyContextState } from "../../../pages/master/pedagogy/context";
import { ExpertContextState } from "../../../pages/master/expert/context";

export interface GroupExpertContainerProps {
  meetingContainerAPI: MeetingContainerAPI | undefined;
  context: ExpertContextState | PedagogyContextState | undefined;
  view: ExpertView;
  validators?: {
    onTalkToGroupLeader?: (group: PopulatedMeetingRoom) => boolean
  }
  stopSpying?: () => void;
  showSelfVideo?: ExpertsToRemotesContainerProps["showSelf"],
  style?: React.CSSProperties;
  className?: string;
}

export const GroupExpertContainer = ({ meetingContainerAPI, context, view, validators, stopSpying, showSelfVideo, style, className }: GroupExpertContainerProps) => {
  const [meetingId] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting?.id);

  const renderHeader = () => {
    let notchText = `Groupe ${view.group?.room.name ?? "inconnu"}`;
    let NotchIcon = ReactIcons.GroupMin;

    if (view.type === "activity_all_leaders") {
      notchText = "Tous les leaders";
      NotchIcon = ReactIcons.CrownFour;
    } else if (view.type === "activity_one_leader") {
      notchText = `Leader du groupe ${view.group?.room.name ?? "inconnu"}`;
      NotchIcon = ReactIcons.CrownMin;
    } else if (view.type === "activity_one_group") {
      NotchIcon = ReactIcons.Incognito;
    }

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "1em",
            alignItems: "center",
            padding: "10px 20px",
            backgroundColor: "#444",
            borderRadius: "0px 0px 10px 10px",
          }}
        >
          <NotchIcon
            style={{
              width: 24,
              height: 24,
              color: "#fff9",
            }}
          />
          <div
            style={{
              fontSize: 20,
              fontWeight: 900,
              color: "white",
            }}
          >
            {notchText.toUpperCase()}
          </div>
        </div>
      </div>
    );
  };

  const renderSingleGroupActions = () => {
    const renderActions = (children: React.ReactNode) => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
          }}
        >
          {children}
        </div>
      </div>
    );

    if (view.type === "activity_one_group_spy") {
      return renderActions(<GroupButton.RevealMyself onClick={() => stopSpying?.()} />);
    }

    if (view.type === "activity_one_group") {
      const { group } = view;
      if (!group) return null; // TODO : improve type, should not happen
      const onlineUsersNumber = group.users.filter((user) => user.sessions.length).length;
      const leader = group.users.find((user) => user.id === group.room.leaderUserId);
      return renderActions(
        <>
          <GroupButton.SendToBoard
            onClick={() => {
              startRoomHighlight(group.room.id);
            }}
            userNumber={onlineUsersNumber}
            disabled={onlineUsersNumber === 0}
          />
          <GroupButton.TalkToLeader
            onClick={() => {
              if (validators?.onTalkToGroupLeader && !validators.onTalkToGroupLeader(group)) return;
              if (!meetingId) return;
              openMeetingActivityRoom({
                id: meetingId,
                audience: {
                  type: MeetingRoomAudienceType.RoomLeader,
                  values: [group.room.id],
                },
              })
            }
            }
            disabled={!leader?.sessions.length}
          />
        </>
      );
    }

    return null;
  };

  const renderSpyOverlay = () => {
    if (view.type !== "activity_one_group_spy") return null;
    return (
      <ReactIcons.Incognito
        style={{
          zIndex: 1,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 300,
          height: 300,
          color: "rgba(255, 255, 255, 0.8)",
        }}
      />
    );
  };

  if (view.type === "normal" || view.type === "groups_overview") {
    return null;
  }

  const extraStyles =
    view.type === "activity_one_group_spy"
      ? {
          backgroundImage: `url("${Icons.IncognitoBackgroundTile}")`,
          backgroundSize: "70px",
          backgroundRepeat: "repeat",
        }
      : {};

  return (
    <div
      className={className}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        color: "#222222",
        ...(style ?? {}),
      }}
    >
      {renderHeader()}
      <div
        style={{
          position: "relative",
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 20,
          minHeight: 0,
          padding: 20,
          marginTop: 20,
          marginBottom: 20,
          backgroundColor: "#555555",
          borderRadius: 8,
          ...extraStyles,
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <ExpertsToRemotesContainer
            meetingContainerAPI={meetingContainerAPI}
            context={context}
            expertSessions={view.experts.filter((data) => data.video).map((data) => data.session)}
            remoteSessions={view.students.filter((data) => data.video).map((data) => data.session)}
            showRemoteGroup={view.type === "activity_all_leaders"}
            showSelf={view.type !== "activity_one_group_spy" ? showSelfVideo : undefined}
          />
        </div>
        {renderSingleGroupActions()}
        {renderSpyOverlay()}
      </div>
    </div>
  );
};
